/**
   * Searches through all arrays of the tree for a value.
   * @param tree : the tree array
   * @param compareFn : This function will receive each node. It's upon you to define which
                        condition is necessary for the match. It must return true if the condition is matched. Example:
                        function(oNode){ if(oNode["Name"] === "AA") return true; }
   * @param isGreedy? : us true to do not stop after the first match, default is false
   * @return an array with references to the nodes for which compareFn was true; In case no node was found an empty array
   *         will be returned
  */

// eslint-disable-next-line @nx/enforce-module-boundaries
import { sortedUniq, uniq } from 'lodash-es';
import { TreeNode } from 'primeng/api';
import { combineLatest, map, Observable, take } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/ban-types
export const searchTree = (tree: any, compareFn: Function, isGreedy = false) => {
    const innerTree = []; // will contain the inner children
    const returnNodes = []; // the nodes array which will returned
    let currentNode; // always the current node

    // 1. loop through all root nodes so we don't touch the tree structure
    for (const keysTree in tree) {
        innerTree.push(tree[keysTree]);
    }

    while (innerTree.length > 0) {
        currentNode = innerTree.pop();
        // check current node
        if (compareFn(currentNode)) {
            returnNodes.push(currentNode);
            if (!isGreedy) {
                return returnNodes;
            }
        } else {
            // find other objects, 1. check all properties of the node if they are arrays
            for (const keysNode in currentNode) {
                // true if the property is an array
                if (Array.isArray(currentNode[keysNode])) {
                    // 2. push all array object to the innerTree to search in those later
                    for (let i = 0; i < currentNode[keysNode].length; i++) {
                        innerTree.push(currentNode[keysNode][i]);
                    }
                }
            }
        }
    }
    return returnNodes; // someone was greedy
};

/**
 * @description Function to check and set partial selection
 *
 * @param node TreeNode to evaluate
 * @param selectedNodesMap Map of selected nodes
 * @returns boolean indicating if the node or any of its children are partially selected
 *
 * We take some code from the saved subtypes to populate the selections in a tree. Items to note
 * is that we need to ensure that parent nodes are also updated correctly in the selectedNodesMap
 * since the tree needs to have the parents of the selected leafs.
 * @example
 * const selectedNodesMap = new Map<string, TreeNode>();
 * for (const key of savedSubtypeIds) {
            let node = flatAllNodes.find(n => n.data === key);
            while (node) {
                selectedNodesMap.set(<string>node.key, node);
                node = node.parent; // Ensure parent nodes are included
            }
        }

        // Process the entire tree to ensure root nodes are updated correctly
        const topLevelNodes = allNodes.filter(node => !node.parent);
        for (const node of topLevelNodes) {
            updatePartialSelection(node, selectedNodesMap);
        }
 */
export const updatePartialSelection = (node: TreeNode, selectedNodesMap: Map<any, TreeNode>): boolean => {
    if (!node.children || node.children.length === 0) {
        // Leaf node, check its selection state
        const isSelected = selectedNodesMap.has(node.key);
        return isSelected;
    }

    let anyChildSelected = false;
    let allChildrenSelected = true;

    for (const child of node.children) {
        const childSelected = updatePartialSelection(child, selectedNodesMap);
        if (childSelected) {
            anyChildSelected = true;
        }
        if (!childSelected || child.partialSelected) {
            allChildrenSelected = false;
        }
    }

    node.partialSelected = anyChildSelected && !allChildrenSelected;

    // Return true if the node itself is selected or any of its children are selected or partially selected
    return selectedNodesMap.has(node.data) || anyChildSelected;
};

/**
 * Recursively expands or collapses nodes in a tree.
 * @param node TreeNode
 * @param expandIt
 */
export const expandRecursive = (node: TreeNode, expandIt: boolean): void => {
    node.expanded = expandIt;
    if (node.children) {
        for (const childNode of node.children) {
            if (childNode.children) {
                expandRecursive(childNode, expandIt);
            }
        }
    }
};

/**
 * Expands or collapses a node and its children in a tree.
 * @param node TreeNode
 */
export const expandNode = (node: TreeNode, isExpanded = true): void => {
    if (node.children) {
        expandRecursive(node, isExpanded);
    }
};

/**
 * Expands all the nodes in a tree
 * @param tree TreeNode[]
 */
export const expandAll = (tree: TreeNode[]): void => {
    for (const node of tree) {
        if (node.children) {
            expandRecursive(node, true);
        }
    }
};

/**
 * Convenience function for @see ExpandNode
 * @param node TreeNode
 */
export const collapseNode = (node: TreeNode): void => {
    expandNode(node, false);
};

/**
 * Convenience function for @see ExpandAll
 * @param tree TreeNode[]
 */
export const collapseAll = (tree: TreeNode[]): void => {
    for (const node of tree) {
        expandRecursive(node, false);
    }
};

export const flattenTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
    const returnNodes: TreeNode[] = [];
    for (const node of nodes) {
        returnNodes.push(node);

        if (node.children) {
            returnNodes.push(...flattenTreeNodes(node.children));
        }
    }

    if (returnNodes.length > 0) {
        return uniq(returnNodes);
    }

    return returnNodes;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const convertToTreeNode = (item: any, transformer: Function): TreeNode => {
    return transformer(item);
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const convertToTreeNodeArray = (items: [], transformer: Function): TreeNode[] => {
    const returnNodes: TreeNode[] = [];
    for (const item of items) {
        returnNodes.push(transformer(item));
    }

    return returnNodes;
};

/**
 * Recursively reset the selection state in tree nodes
 * @param nodes TreeNode[]
 */
export const resetNodeSelection = (nodes: TreeNode[]): void => {
    for (const node of nodes) {
        node.partialSelected = false; // Clear partial selection state
        node.expanded = false; // Optionally collapse nodes
        if (node.children) {
            resetNodeSelection(node.children);
        }
    }
};

/**
 * Finds all the parent nodes of a given node
 * @param node TreeNode
 * @returns TreeNode[]
 */
export const getAllParentNodes = (node: TreeNode): TreeNode[] => {
    const parentNodes: TreeNode[] = [];
    let currentNode = node;
    while (currentNode.parent) {
        parentNodes.push(currentNode.parent);
        currentNode = currentNode.parent;
    }
    return parentNodes;
};

/**
 * Finds the top-level node in a hierarchy of TreeNodes
 *
 * @param node  TreeNode
 * @returns Top-level TreeNoed
 */
export const getTopLevelParentNode = (node: TreeNode): TreeNode => {
    if (node.parent) {
        return getTopLevelParentNode(node.parent);
    }
    return node;
};

/**
 * For Trees that are multi-select where the selectionMode=checkbox, It's necessary to include the parent
 *  - Always pass in the parent of the node you're evaluating for this to work properly
 *  - if all the children are selected. This will verify and modify the selectedNodes param with the parent
 *
 * @param selectedNodes TreeNode[]
 * @param node          TreeNode
 * @param recursive     Boolean Defaults to false
 */
export const checkedParentNodesSelection = (selectedNodes: TreeNode[], node: TreeNode, recursive = false) => {
    // Keep track of what nodes have been processed
    const processedKeys = new Set<string>();
    const checkParentNodes = (node: TreeNode) => {
        if (node && node.children) {
            if (processedKeys.has(<string>node.key)) {
                return;
            }

            processedKeys.add(<string>node.key);
            const allChildrenSelected = node.children.every(child => selectedNodes.includes(child));

            if (allChildrenSelected) {
                // Add the parent node if not already included
                if (!selectedNodes.includes(node)) {
                    selectedNodes.push(node);
                }
            } else {
                // Remove the parent node if not all children are selected
                const index = selectedNodes.indexOf(node);
                if (index > -1) {
                    selectedNodes.splice(index, 1);
                }
            }

            // Process the next parent
            if (recursive && node.parent) {
                checkParentNodes(node.parent);
            }
        }
    };

    checkParentNodes(node);
};

/**
 * Builds a tree from a flat array of objects
 * @param data The data to build the tree from
 * @param hierarchyTree The hierarchy of the tree
 * @param customLabelFunc A function to customize the label of the tree node
 */
export function buildTreeFromHierarchy<T>(data: T[], hierarchyTree: (keyof T)[], customLabelFunc?: (key: string, label: string, quarterFormat?: string) => string, quarterFormat?: string): TreeNode[] {
    const tree: TreeNode[] = [];
    const nodeMap = new Map<string, TreeNode>();
    const customId = (item: T, hierarchy: (keyof T)[], hierarchyPosition: number): string => {
        let key = '';
        for (let i = 0; i <= hierarchyPosition; i++) {
            key += item[hierarchy[i]];
        }
        return key;
    };

    for (const item of data) {
        let parentNode: TreeNode | undefined;
        for (const [index, key] of hierarchyTree.entries()) {
            const nodeId = customId(item, hierarchyTree, index);
            let node = nodeMap.get(nodeId);
            if (!node) {
                node = {
                    key: nodeId,
                    label: customLabelFunc ? customLabelFunc(key as string, item[key] as string, quarterFormat as string) : (item[key] as string),
                    selectable: true,
                    parent: parentNode,
                    children: index < hierarchyTree.length - 1 ? [] : undefined,
                    leaf: index === hierarchyTree.length - 1,
                    data: []
                };
                nodeMap.set(nodeId, node);
                if (parentNode) {
                    parentNode?.children?.push(node);
                } else {
                    tree.push(node);
                }
            }
            parentNode = node;
            parentNode.data.push(item);
        }
    }
    return tree;
}

/**
 * Auxiliary function to recursively process children
 * @param mainNode Node on highest level
 * @param possibleParents possible parents of the selected nodes to be inserted as children
 * @param selectedNodes selected nodes to be inserted as children
 */
function processChildren(mainNode: TreeNode, possibleParents: TreeNode[], selectedNodes: TreeNode[]): TreeNode[] {
    const possibleParentsIds = new Set(possibleParents.map(item => item.data ?? item.key));
    const selectedNodesIds = new Set(selectedNodes.map(item => item.data ?? item.key));

    return mainNode.children ?
            mainNode.children.filter(child => {
                if (child.children && child.children.length > 0) {
                    child.children = processChildren(child, possibleParents, selectedNodes);
                }
                return possibleParentsIds.has(child.data) || possibleParentsIds.has(child.key) || selectedNodesIds.has(child.data) || selectedNodesIds.has(child.key);
            })
        :   [];
}

/**
 * Clone node without parent information to avoid circular references that blocks the cloning
 * @param node Node to be cloned
 * @returns cloned node without parent
 */
export function cloneTreeNodeWithoutParent(node: TreeNode): TreeNode {
    // Actual node without parent reference
    const clone: TreeNode = { ...node, parent: undefined };

    // Verify for children and clones recursively
    if (node.children && node.children.length > 0) {
        clone.children = node.children.map(child => cloneTreeNodeWithoutParent(child));
    }

    return clone;
}

export function rebuildTreeFromChildren(selectedNodes: TreeNode<any>[]): TreeNode[] {
    const mainNodes: { [key: string]: TreeNode<any> } = {};

    // Get the top level nodes and sets on the mainNodes mapping
    const topLevelNodes = selectedNodes.map(item => getTopLevelParentNode(item));
    for (const topLevelNode of topLevelNodes) {
        if (!mainNodes[topLevelNode.key!]) {
            mainNodes[topLevelNode.key!] = topLevelNode;
        }
    }

    // All possible parent nodes without top levels and unique
    const allPossibleParents = sortedUniq(selectedNodes.flatMap(item => getAllParentNodes(item)).filter(parent => !topLevelNodes.includes(parent)));

    // Iteration of each main node reconstructing children
    // Needs to use a copy of the nodes to not change the original reference on DOM
    const objValues = Object.values(mainNodes);
    const mainNodesCopy = objValues
        .map(node => cloneTreeNodeWithoutParent(node))
        .map(node => {
            return { ...node, children: processChildren(node, allPossibleParents, selectedNodes) };
        });

    // Return the final array
    return mainNodesCopy;
}

/**
 * Selects all the available options minus the ones that are already selected
 * @param allIds$ All the available options
 * @param storedSelectionsIds$ The selected options
 * @returns An observable that emits the inverse of the selected ids
 */
export function selectInverseSelectedNodes(allIds$: Observable<string[] | undefined>, storedSelectionsIds$: Observable<string[] | undefined>): Observable<string[]> {
    return combineLatest([allIds$, storedSelectionsIds$ as Observable<string[]>]).pipe(
        take(1),
        map(([allSelections, selected]) => allSelections?.filter((rs: string) => !selected?.includes(rs)) as string[])
    );
}
